import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { content, type DeckPassiveId } from '@cards/cards';
import { Alert, Button, Modal, PanelHeader } from '../ui/kit/index.js';
import { DeckPassiveDetails } from '../ui/DeckPassiveDetails.js';
import { navigateTo } from '../client/navigation.js';
import type { DeckApi, SavedDeckDto } from './api.js';
import { DeckGlyphStrip } from './DeckGlyphStrip.js';
import { deckErrorMessage, isAbortError } from './messages.js';

export interface SelectedDeckSummary {
  readonly deckId: string;
  readonly revision: number;
  readonly name: string;
  readonly cardIds: readonly string[];
  readonly passiveId: DeckPassiveId | null;
}

export interface LobbyDeckModalProps {
  api: DeckApi;
  csrfToken: string;
  rememberedDeckId?: string | null;
  onClose: () => void;
  onChosen: (deck: SelectedDeckSummary) => void;
  navigate?: (path: string) => void;
}

function defaultNavigate(path: string): void {
  navigateTo(path);
}

/** Only complete, non-archived owner decks can be picked before a match. */
function isReady(deck: SavedDeckDto): boolean {
  return deck.validation.ready && deck.archivedAt === null;
}

export function LobbyDeckModal({
  api,
  csrfToken,
  rememberedDeckId,
  onClose,
  onChosen,
  navigate = defaultNavigate,
}: LobbyDeckModalProps): ReactElement {
  const [decks, setDecks] = useState<readonly SavedDeckDto[] | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [busyDeckId, setBusyDeckId] = useState<string | undefined>();
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  const load = useCallback(async (signal?: AbortSignal): Promise<void> => {
    setLoading(true);
    setError(undefined);
    try {
      const library = await api.list(false, signal);
      const ready = library.decks.filter(isReady);
      const remembered = library.rememberedDeckId;
      const ordered = remembered === null || remembered === undefined
        ? ready
        : [...ready].sort((left, right) =>
            Number(right.id === remembered) - Number(left.id === remembered));
      setDecks(ordered);
    } catch (reason) {
      if (!isAbortError(reason)) setError(deckErrorMessage(reason));
    } finally {
      if (signal?.aborted !== true) setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  async function choose(deck: SavedDeckDto): Promise<void> {
    setBusyDeckId(deck.id);
    setError(undefined);
    try {
      await api.setPreference(deck.id, csrfToken);
      onChosen({
        deckId: deck.id,
        revision: deck.revision,
        name: deck.name,
        cardIds: deck.cardIds,
        passiveId: deck.passiveId,
      });
      onClose();
    } catch (reason) {
      setError(deckErrorMessage(reason));
      setBusyDeckId(undefined);
    }
  }

  return (
    <Modal
      as="section"
      layer="choice"
      className="lobby-deck-modal"
      overlayTestId="lobby-deck-modal-overlay"
      testId="lobby-deck-modal"
      labelledBy="lobby-deck-modal-title"
      initialFocusRef={closeButtonRef}
      onClose={onClose}
    >
      <PanelHeader
        className="lobby-deck-modal-header"
        title="Выберите колоду"
        headingLevel={2}
        headingId="lobby-deck-modal-title"
        description="Показаны готовые колоды. Незавершённые собираются на странице колод."
        actions={(
          <Button
            variant="ghost"
            size="sm"
            ref={closeButtonRef}
            className="lobby-deck-modal-close"
            data-testid="lobby-deck-modal-close"
            aria-label="Закрыть выбор колоды"
            onClick={onClose}
          >
            ×
          </Button>
        )}
      />

      {error !== undefined ? (
        <Alert className="lobby-deck-modal-error" data-testid="lobby-deck-modal-error">{error}</Alert>
      ) : null}

      <div className="lobby-deck-modal-body" data-testid="lobby-deck-modal-body">
        {loading ? (
          // Skeleton rows keep the dialog at its final size while the library
          // loads, so the list fills in instead of the modal jumping taller.
          <div role="status" aria-label="Загружаем колоды…" data-testid="lobby-deck-modal-loading">
            <ul className="lobby-deck-modal-list" aria-hidden="true">
              {[0, 1, 2, 3, 4, 5].map((row) => (
                <li key={row} className="lobby-deck-modal-row lobby-deck-modal-row-skeleton">
                  <span className="lobby-deck-modal-skeleton-glyphs" />
                  <span className="lobby-deck-modal-row-main lobby-deck-modal-skeleton-name" />
                  <span className="lobby-deck-modal-skeleton-action" />
                </li>
              ))}
            </ul>
          </div>
        ) : decks === undefined ? (
          <div className="lobby-deck-modal-status">
            <Button variant="secondary" size="compact" onClick={() => { void load(); }}>
              Повторить загрузку
            </Button>
          </div>
        ) : decks.length === 0 ? (
          <div className="lobby-deck-modal-empty" data-testid="lobby-deck-modal-empty">
            <p>Нет готовых колод. Соберите колоду на странице колод.</p>
          </div>
        ) : (
          <ul className="lobby-deck-modal-list" aria-label="Готовые колоды">
            {decks.map((deck) => {
              const remembered = rememberedDeckId === deck.id;
              const passivePresentation = deck.passiveId === null
                ? undefined
                : content.deckPassiveCatalog.get(deck.passiveId);
              return (
                <li
                  key={deck.id}
                  className="lobby-deck-modal-row"
                  data-testid={`lobby-deck-${deck.id}`}
                  data-remembered={remembered ? 'true' : 'false'}
                >
                  <DeckGlyphStrip cardIds={deck.cardIds} />
                  <div className="lobby-deck-modal-row-main">
                    <span className="lobby-deck-modal-row-name">{deck.name}</span>
                    {passivePresentation === undefined ? null : (
                      <DeckPassiveDetails
                        presentation={passivePresentation}
                        density="compact"
                        testId={`lobby-deck-${deck.id}-passive`}
                      />
                    )}
                  </div>
                  <Button
                    variant={remembered ? 'primary' : 'secondary'}
                    size="compact"
                    data-testid={`choose-lobby-deck-${deck.id}`}
                    disabled={busyDeckId !== undefined}
                    onClick={() => { void choose(deck); }}
                  >
                    {busyDeckId === deck.id ? 'Выбираем…' : remembered ? 'Выбрано' : 'Выбрать'}
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <Modal.Actions className="lobby-deck-modal-footer">
        <Button
          variant="secondary"
          size="compact"
          data-testid="lobby-deck-modal-manage"
          onClick={() => navigate('/decks')}
        >
          Мои колоды
        </Button>
      </Modal.Actions>
    </Modal>
  );
}
